"use client";

import type { DefaultError, UseMutationOptions } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import { useMemo } from "react";

import { useSession } from "../../session/hooks/useSession";
import type { paths } from "../generated/schema";
import { createOpenApiClient } from "../openApiClient";
import { useHeadersValues } from "./useHeadersValues";

// Type helpers for extracting paths that have POST/PUT/DELETE methods
type HttpMethod = "POST" | "PUT" | "DELETE" | "PATCH";

type PathsWithMethod<M extends HttpMethod> = {
  [P in keyof paths]: paths[P] extends { [K in Lowercase<M>]: unknown }
    ? P
    : never;
}[keyof paths];

// Extract request body for a method
type RequestBody<
  P extends keyof paths,
  M extends HttpMethod,
> = paths[P] extends {
  [K in Lowercase<M>]: {
    requestBody: { content: { "application/json": infer B } };
  };
}
  ? B
  : undefined;

// Extract parameters for a method
type MethodParams<
  P extends keyof paths,
  M extends HttpMethod,
> = paths[P] extends { [K in Lowercase<M>]: { parameters: infer Params } }
  ? Params
  : undefined;

// Extract successful response type for a method
type MethodResponse<
  P extends keyof paths,
  M extends HttpMethod,
> = paths[P] extends {
  [K in Lowercase<M>]: {
    responses: { 200: { content: { "application/json": infer R } } };
  };
}
  ? R
  : void;

// Combined variables type for mutations
type MutationVariables<P extends keyof paths, M extends HttpMethod> = {
  params?: MethodParams<P, M>;
  body?: RequestBody<P, M>;
};

export type UseOpenApiMutationOptions<
  P extends keyof paths,
  M extends HttpMethod,
  TData = MethodResponse<P, M>,
  TVariables = MutationVariables<P, M>,
> = Omit<UseMutationOptions<TData, DefaultError, TVariables>, "mutationFn"> & {
  urlBackendPath: P;
  method: M;
};

export function useOpenApiMutation<
  M extends HttpMethod,
  P extends PathsWithMethod<M>,
  TData = MethodResponse<P, M>,
>({
  urlBackendPath,
  method,
  ...options
}: UseOpenApiMutationOptions<P, M, TData>) {
  const headersValues = useHeadersValues();
  const { setToken } = useSession();

  const client = useMemo(
    () =>
      createOpenApiClient({
        headersValues: headersValues.current,
        onTokenRefresh: setToken,
      }),
    [headersValues.current.token, headersValues.current.locale, setToken],
  );

  return useMutation({
    mutationFn: async (variables: MutationVariables<P, M>) => {
      // Use type assertion to handle the dynamic method selection
      const methodFn =
        method === "POST"
          ? client.POST
          : method === "PUT"
            ? client.PUT
            : method === "DELETE"
              ? client.DELETE
              : client.PATCH;

      const { data, error } = await (methodFn as any)(urlBackendPath, {
        params: variables.params,
        body: variables.body,
      });

      if (error) {
        const apiError = error as {
          error?: string;
          message?: string;
          code?: number;
        };
        const normalizedError = new Error(
          apiError.error ?? apiError.message ?? "An error occurred",
        );
        (normalizedError as any).code = apiError.code;
        throw normalizedError;
      }
      return data as TData;
    },
    ...options,
  });
}
